import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useGetCurrentDoc } from 'src/hooks';

export const SHOW_DELAY = 1000;
export const RECONNECT_TIMEOUT = 1000 * 15;

export const RECONNECT_SNACKBAR_KEY = 'document-reconnect-snackbar';
export const DISCONNECTED_SNACKBAR_KEY = 'document-disconnected-snackbar';

function NoAction(): JSX.Element | null {
  return null;
}

type UseDocumentReconnectSnackbarOptions = {
  connected: boolean;
  loading: boolean;
};

export function useDocumentReconnectSnackbar({
  connected,
  loading,
}: UseDocumentReconnectSnackbarOptions): boolean {
  const { document } = useGetCurrentDoc();
  const [editLocked, setEditLocked] = useState(false);
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const isAlineDoc = document?.latestVersion.__typename === 'DocumentVersion';

  useEffect(() => {
    if (!isAlineDoc || connected || loading) return;

    /**
     * Show the reconnect snackbar after a small delay so that expected reconnects (e.g. when the document access token expires)
     * don't show a snackbar unnecessarily.
     */
    const reconnectTimeoutId = setTimeout(() => {
      enqueueSnackbar({
        action: <NoAction />,
        key: RECONNECT_SNACKBAR_KEY,
        message:
          "You have been disconnected from the document. Your changes won't be saved until you reconnect. Attempting to reconnect automatically, or you can refresh.",
        persist: true,
        variant: 'warning',
      });
    }, SHOW_DELAY);
    const showTimeoutId = setTimeout(() => {
      setEditLocked(true);
      closeSnackbar(RECONNECT_SNACKBAR_KEY);
      enqueueSnackbar({
        action: <NoAction />,
        key: DISCONNECTED_SNACKBAR_KEY,
        message:
          'You have been disconnected from the document. Reconnect to make further changes. Attempting to reconnect automatically, or you can refresh.',
        persist: true,
        variant: 'error',
      });
    }, RECONNECT_TIMEOUT);

    return () => {
      clearTimeout(showTimeoutId);
      clearTimeout(reconnectTimeoutId);

      closeSnackbar(RECONNECT_SNACKBAR_KEY);
      closeSnackbar(DISCONNECTED_SNACKBAR_KEY);

      setEditLocked(false);
    };
  }, [connected, enqueueSnackbar, closeSnackbar, loading, isAlineDoc]);

  return editLocked;
}
